import { supabase } from '@/integrations/supabase/client';

export interface AgentCategoryRecord {
  id: string;
  name: string;
  description: string | null;
  usageCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AgentCategoryCatalogue {
  categories: AgentCategoryRecord[];
  managed: boolean;
}

interface AgentCategoryRpcRow {
  id: string;
  name: string;
  description: string | null;
  usage_count: number | string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface RpcError {
  code?: string;
  message: string;
}

const categoryDb = supabase as unknown as {
  rpc: (
    functionName:
      | 'get_agent_categories'
      | 'create_agent_category'
      | 'rename_agent_category'
      | 'delete_agent_category',
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: RpcError | null }>;
};

function isMissingCatalogue(error: RpcError) {
  return error.code === 'PGRST202'
    || error.code === '42883'
    || error.message.includes('get_agent_categories');
}

function toCategory(row: AgentCategoryRpcRow): AgentCategoryRecord {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    usageCount: Number(row.usage_count || 0),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function fetchLegacyCategories(): Promise<AgentCategoryRecord[]> {
  const { data, error } = await supabase
    .from('simulation_agents')
    .select('agent_category')
    .not('agent_category', 'is', null);

  if (error) throw new Error(`Unable to load agent categories: ${error.message}`);

  const labels = new Set<string>();
  (data || []).forEach((row: { agent_category: string | null }) => {
    const value = (row.agent_category || '').trim();
    if (value) labels.add(value);
  });

  return Array.from(labels)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      id: `legacy:${name.toLowerCase()}`,
      name,
      description: null,
      usageCount: 0,
      createdAt: null,
      updatedAt: null,
    }));
}

export async function fetchAgentCategoryCatalogue(): Promise<AgentCategoryCatalogue> {
  const { data, error } = await categoryDb.rpc('get_agent_categories');

  if (error) {
    if (isMissingCatalogue(error)) {
      return { categories: await fetchLegacyCategories(), managed: false };
    }
    throw new Error(`Unable to load agent categories: ${error.message}`);
  }

  return {
    categories: ((data || []) as AgentCategoryRpcRow[]).map(toCategory),
    managed: true,
  };
}

export async function createAgentCategory(
  name: string,
  description?: string,
): Promise<AgentCategoryRecord> {
  const { data, error } = await categoryDb.rpc('create_agent_category', {
    p_name: name,
    p_description: description || null,
  });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('The category was not returned after creation.');
  return toCategory(row as AgentCategoryRpcRow);
}

export async function renameAgentCategory(
  categoryId: string,
  name: string,
  description?: string,
): Promise<AgentCategoryRecord> {
  const { data, error } = await categoryDb.rpc('rename_agent_category', {
    p_category_id: categoryId,
    p_name: name,
    p_description: description || null,
  });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('The category was not returned after updating.');
  return toCategory(row as AgentCategoryRpcRow);
}

export async function deleteAgentCategory(categoryId: string): Promise<void> {
  const { data, error } = await categoryDb.rpc('delete_agent_category', {
    p_category_id: categoryId,
  });
  if (error) throw new Error(error.message);
  if (data !== true) throw new Error('The category could not be deleted.');
}
